import { useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useQuoteDetails } from '@lp/features/RoutePlanner/hooks/useQuoteDetails';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { getLegProviders } from '@lp/features/RoutePlanner/utils';
import { ACTIVE_GROUND_TRIP_STATUSES } from '@lp/features/RoutePlanner/config';
import { RouteTypeEnum } from '@/api/apis/route';
import { GroundTripStatus } from '@/api/apis/drive-service';
import { QuoteStatus, ReviewType, RoutePlanStatus } from '@/api/apis/case';
import { RouteBuilderPermissions } from '@/permissions';
import { useDriveTripsByIdsQuery } from '@/queries/drive-service';

const GROUND_APPROVE_TRIPS_STATUSES = [
  GroundTripStatus.Planned,
  GroundTripStatus.Merged,
  ...ACTIVE_GROUND_TRIP_STATUSES,
];

export const useLogisticsPermissions = () => {
  const { checkPermission } = useContext(AuthContext);
  const {
    routePlanningStatus,
    reviews,
    txCenterDetails: txCenter,
    quote,
    opoRequest,
    isExpeditedCase,
    useFixedPriceFlow,
  } = useCaseDetailsWithReviews();
  const { quoteDetails } = useQuoteDetails();
  const { needAviationApproval, isTmdxDriveCompatible, groundTripIds } = useLogisticsData();
  const mainRouteData = useRouteDetails(RouteTypeEnum.Main);
  const dryRunRouteData = useRouteDetails(RouteTypeEnum.DryRun);
  const tripResults = useDriveTripsByIdsQuery(groundTripIds);

  const mainRouteProviders = useMemo(() => getLegProviders(mainRouteData.legs), [mainRouteData.legs]);
  const dryRunRouteProviders = useMemo(() => getLegProviders(dryRunRouteData.legs), [dryRunRouteData.legs]);

  const isSendToAviation = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionSendToAviationApprove);
    const isRoutePlanning = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const hasTmdxAvia = mainRouteProviders.hasTmdxAvia || dryRunRouteProviders.hasTmdxAvia;

    return hasTmdxAvia && hasPermission && isRoutePlanning && needAviationApproval;
  }, [
    checkPermission,
    mainRouteProviders.hasTmdxAvia,
    dryRunRouteProviders.hasTmdxAvia,
    routePlanningStatus,
    needAviationApproval,
  ]);

  const isAviationApprove = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionAviationApprove);
    const isRoutePlanning = routePlanningStatus === RoutePlanStatus.WaitingForAviationApproval;

    return hasPermission && isRoutePlanning;
  }, [checkPermission, routePlanningStatus]);

  const isSendQuoteInternal = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionSendQuoteInternal);
    const isRouteStart = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;
    const isCaseWithoutTx = (opoRequest || !!isExpeditedCase) && !txCenter;
    const isNoQuotePartner =
      !quote?.status ||
      (quote?.status &&
        [QuoteStatus.NoQuote, QuoteStatus.Created, QuoteStatus.Recalled, QuoteStatus.ApprovedInternally].includes(
          quote.status,
        ));
    const hasPartnerLegs =
      mainRouteProviders.hasSupplementalLiftAvia ||
      mainRouteProviders.hasTmdxAffiliateGround ||
      mainRouteProviders.hasSelfServicedGround;

    const isOnlyPartnerFlow = !mainRouteProviders.hasTmdxAvia && hasPartnerLegs && isRouteStart && isNoQuotePartner;
    const isTmdxFlow = mainRouteProviders.hasTmdxAvia && isRouteStart && isNoQuotePartner && !needAviationApproval;
    const isSendQuoteMainFlow = isOnlyPartnerFlow || isTmdxFlow;

    return hasPermission && isSendQuoteMainFlow && !useFixedPriceFlow && !isCaseWithoutTx;
  }, [
    opoRequest,
    isExpeditedCase,
    txCenter,
    checkPermission,
    mainRouteProviders.hasSupplementalLiftAvia,
    mainRouteProviders.hasTmdxAffiliateGround,
    mainRouteProviders.hasSelfServicedGround,
    mainRouteProviders.hasTmdxAvia,
    quote?.status,
    routePlanningStatus,
    useFixedPriceFlow,
    needAviationApproval,
  ]);

  const isRoutePlanningDone = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionRouteComplete);
    const isRouteStart = routePlanningStatus === RoutePlanStatus.RoutePlanningStart;

    const isCaseWithoutTx = (opoRequest || !!isExpeditedCase) && !txCenter;
    const isQuoteStatus =
      quote?.status && [QuoteStatus.Rejected, QuoteStatus.Approved, QuoteStatus.SentToTXCenter].includes(quote.status);

    const hasPartnerLegs =
      mainRouteProviders.hasSupplementalLiftAvia ||
      mainRouteProviders.hasTmdxAffiliateGround ||
      mainRouteProviders.hasSelfServicedGround;
    const isPartnerFlow = hasPartnerLegs && (isCaseWithoutTx || useFixedPriceFlow || isQuoteStatus);
    const isTxProvider = mainRouteProviders.hasTxCenterGround || mainRouteProviders.isTxCenterAvia;
    const isOnlyTxCenterPartner = !mainRouteProviders.hasTmdxAvia && !hasPartnerLegs && isTxProvider;
    const isOnlyTxCenterProviderFlow = isOnlyTxCenterPartner && isRouteStart;
    const isTmdxFlow =
      mainRouteProviders.hasTmdxAvia &&
      !needAviationApproval &&
      (isCaseWithoutTx || useFixedPriceFlow || isQuoteStatus);

    return hasPermission && isRouteStart && (isPartnerFlow || isOnlyTxCenterProviderFlow || isTmdxFlow);
  }, [
    checkPermission,
    routePlanningStatus,
    opoRequest,
    isExpeditedCase,
    txCenter,
    quote?.status,
    mainRouteProviders.hasSupplementalLiftAvia,
    mainRouteProviders.hasTmdxAffiliateGround,
    mainRouteProviders.hasSelfServicedGround,
    mainRouteProviders.hasTmdxAvia,
    mainRouteProviders.hasTxCenterGround,
    mainRouteProviders.isTxCenterAvia,
    useFixedPriceFlow,
    needAviationApproval,
  ]);

  const isReviewQuote = useMemo(() => {
    const hasAirQuotePermission = checkPermission(RouteBuilderPermissions.ActionReviewAirQuote);
    const hasGroundQuotePermission = checkPermission(RouteBuilderPermissions.ActionReviewGroundQuote);
    const isAirQuote = quoteDetails && !!quoteDetails.airQuote;
    const isQuoteCreated = quote?.status === QuoteStatus.Created;
    const hasPermission = isAirQuote ? hasAirQuotePermission : hasGroundQuotePermission;

    return hasPermission && isQuoteCreated && !useFixedPriceFlow;
  }, [checkPermission, quoteDetails, quote?.status, useFixedPriceFlow]);

  const isGroundApprove = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionGroundApprove);
    const isPendingBooking = routePlanningStatus === RoutePlanStatus.PendingResourcesBooking;
    const hasGroundReview = reviews.some((r) => r.type === ReviewType.Ground);
    const hasLegacyGroundLegs =
      mainRouteProviders.hasTmdxAffiliateGround || dryRunRouteProviders.hasTmdxAffiliateGround;
    const hasTmdxDriveLegs = !!groundTripIds.length;

    const hasGroundLegs = isTmdxDriveCompatible ? hasTmdxDriveLegs : hasLegacyGroundLegs;

    return hasGroundLegs && hasPermission && isPendingBooking && !hasGroundReview;
  }, [
    checkPermission,
    routePlanningStatus,
    reviews,
    mainRouteProviders.hasTmdxAffiliateGround,
    dryRunRouteProviders.hasTmdxAffiliateGround,
    isTmdxDriveCompatible,
    groundTripIds,
  ]);

  const isBookResources = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionBookResources);
    const isCorrectRouteStatus =
      routePlanningStatus &&
      [RoutePlanStatus.RoutePlanningDone, RoutePlanStatus.PendingResourcesBooking].includes(routePlanningStatus);
    const isNoQuote = !quote?.status || quote.status === QuoteStatus.NoQuote || quote.status === QuoteStatus.Recalled;
    const isQuoteApproved =
      quote?.status &&
      [QuoteStatus.ApprovedInternally, QuoteStatus.Approved, QuoteStatus.SentToTXCenter].includes(quote.status);

    return hasPermission && isCorrectRouteStatus && (isNoQuote || isQuoteApproved);
  }, [checkPermission, routePlanningStatus, quote?.status]);

  const isLegacyConfirmGroundDisabled = useMemo(() => {
    const groundLegs = [...mainRouteProviders.tmdxAffiliateLegs, ...dryRunRouteProviders.tmdxAffiliateLegs];

    if (groundLegs.length) {
      return groundLegs.some((leg) => {
        return !(
          leg.driver &&
          (leg.phoneNumber || leg.email) &&
          leg.vehicleType &&
          leg.plateNumber &&
          leg.reservationNumber
        );
      });
    }

    return false;
  }, [mainRouteProviders.tmdxAffiliateLegs, dryRunRouteProviders.tmdxAffiliateLegs]);

  const isTmdxDriveConfirmGroundDisabled = useMemo(() => {
    const groundTrips = tripResults.map((r) => r.data).filter((trip) => Boolean(trip)) || [];

    if (groundTrips.length) {
      return groundTrips.some((trip) => !GROUND_APPROVE_TRIPS_STATUSES.includes(trip.status));
    }

    return false;
  }, [tripResults]);

  const isSendQuoteToTx = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionSendQuote);
    const isQuoteApproved = quote?.status === QuoteStatus.ApprovedInternally;

    return hasPermission && isQuoteApproved && !useFixedPriceFlow;
  }, [useFixedPriceFlow, checkPermission, quote?.status]);

  const isApproveQuoteByTXC = useMemo(() => {
    const hasPermission = checkPermission(RouteBuilderPermissions.ActionApproveQuoteByTx);
    const isQuoteSentToTXC = quote?.status === QuoteStatus.SentToTXCenter;
    const isLastRouteStatus = routePlanningStatus === RoutePlanStatus.ResourcesBooked;

    return hasPermission && isQuoteSentToTXC && isLastRouteStatus;
  }, [checkPermission, quote, routePlanningStatus]);

  const isBookingPendingStatus = routePlanningStatus === RoutePlanStatus.PendingResourcesBooking;

  return {
    isSendToAviation,
    isAviationApprove,
    isSendQuoteInternal,
    isRoutePlanningDone,
    isReviewQuote,
    isGroundApprove,
    isBookResources,
    isSendQuoteToTx,
    isBookingPendingStatus,
    isApproveQuoteByTXC,
    isConfirmGroundDisabled: isTmdxDriveCompatible ? isTmdxDriveConfirmGroundDisabled : isLegacyConfirmGroundDisabled,
    isTmdxDriveCompatible,
  };
};
